'use client';

import { TERMS_OF_SERVICE } from '@shared/constants';

/**
 * Terms of Service page component.
 *
 * Presents a comprehensive South African telehealth ToS in plain language,
 * including emergency disclaimers, informed consent, and POPIA-aligned terms.
 */
export function TermsPage() {
  const { metadata, emergencyDisclaimer, keyPoints, sections } = TERMS_OF_SERVICE;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-12">
      <div className="max-w-4xl mx-auto">
        <article className="space-y-8" aria-label={metadata.articleAriaLabel}>
          <header className="space-y-3">
            <h1 className="text-4xl font-bold text-foreground">{metadata.title}</h1>
            <p className="text-muted-foreground">
              {metadata.introduction} Effective date: {metadata.effectiveDate}. Last updated:{' '}
              {metadata.lastUpdated}.
            </p>
            <p className="text-muted-foreground">
              Please also review our{' '}
              <a href={metadata.privacyPolicyPath} className="text-brand hover:underline font-semibold">
                Privacy Policy
              </a>
              .
            </p>
          </header>

          <section
            aria-label="Emergency disclaimer"
            className="rounded-lg border-2 border-red-600 bg-red-50 p-6"
          >
            <h2 className="text-2xl font-bold text-red-700 mb-3">{emergencyDisclaimer.title}</h2>
            {emergencyDisclaimer.messages.map((message, index) => (
              <p
                key={message}
                className={`text-red-800 ${index === 0 ? 'font-semibold' : 'mt-3'}`}
              >
                {message}
              </p>
            ))}
          </section>

          <section className="rounded-lg border bg-accent/20 p-6" aria-label="Key points">
            <h2 className="text-2xl font-semibold mb-4">Key Points</h2>
            <ul className="space-y-3">
              {keyPoints.map((keyPoint) => (
                <li key={keyPoint} className="flex items-start gap-3 text-accent-foreground/90">
                  <span className="font-bold text-accent-foreground">-</span>
                  <span>{keyPoint}</span>
                </li>
              ))}
            </ul>
          </section>

          <nav aria-label="Table of Contents" className="rounded-lg bg-card p-6 border">
            <h2 className="text-xl font-semibold mb-3">Table of Contents</h2>
            <ul className="space-y-2">
              {sections.map((section) => (
                <li key={section.id}>
                  <a
                    href={`#${section.id}`}
                    className="text-brand hover:underline focus:outline-none focus:ring-2 focus:ring-brand rounded"
                  >
                    {section.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <main className="space-y-8">
            {sections.map((section) => (
              <section key={section.id} id={section.id} className="space-y-4">
                <h2 className="text-2xl font-semibold">{section.title}</h2>
                {section.paragraphs.map((paragraph) => (
                  <p
                    key={`${section.id}-${paragraph.text}`}
                    className={
                      'preserveLineBreaks' in paragraph && paragraph.preserveLineBreaks
                        ? 'whitespace-pre-line'
                        : ''
                    }
                  >
                    {paragraph.text}
                  </p>
                ))}
                {'bullets' in section ? (
                  <ul className="list-disc pl-5 space-y-2 text-muted-foreground">
                    {section.bullets.map((item) => (
                      <li key={`${section.id}-${item}`}>{item}</li>
                    ))}
                  </ul>
                ) : null}
              </section>
            ))}
          </main>
        </article>
      </div>
    </div>
  );
}
